import type { TranslateService } from '@ngx-translate/core';
import type { WebWidgetModel, WidgetMetaData } from '@web/types';
import type { EduLoginButtonEntity, EduLoginButtonItem } from '@sites/entity-core';

import { createId } from '@paralleldrive/cuid2';

import { Widgets } from '@common/enums';
import { defaultWidgetParams, defaultWidgetPermission } from '@common/constants';
import { createEduLoginButton } from '@sites/entity-core';

import { DEFAULT_BUTTON_ICON } from '@web/widgets/common/base/base-button/base-button.const';

import { EduLoginButtonControlComponent } from '../components/edu-login-button-control/edu-login-button-control.component';
import { EduLoginButtonViewComponent } from '../components/edu-login-button-view/edu-login-button-view.component';

// DEFAULT COMPONENT SETTINGS — a fork of the button widget's model. The data
// factory comes from the forked `@sites/entity-core` entity (`createEduLoginButton`),
// which already seeds the fixed `/courses/auth/login` link on the button item;
// the host-only concerns (localised label, generated `elementId`, default icon
// SVG) are passed in via `buttonOverrides` here.
const eduLoginButtonData = (translate: TranslateService, meta?: WidgetMetaData): EduLoginButtonEntity['data'] => {
    const buttonOverrides: Partial<EduLoginButtonItem> = {
        elementId: createId(),
        buttonIcon: { ...DEFAULT_BUTTON_ICON },
        label: translate.instant('widget_edu_login_button_default_label'),
    };

    const entity =
        meta?.type === 'zero'
            ? createEduLoginButton('zero', { buttonOverrides })
            : createEduLoginButton('default', { buttonOverrides });

    return entity.data as EduLoginButtonEntity['data'];
};
// END DEFAULT SETTINGS

export const eduLoginButtonWidgetModel: WebWidgetModel = {
    componentView: EduLoginButtonViewComponent,
    componentControl: EduLoginButtonControlComponent,
    name: Widgets.EDU_LOGIN_BUTTON,
    label: 'widget_edu_login_button_label',
    data: eduLoginButtonData,
    permission: defaultWidgetPermission,
    // `isEducation: true` + `disabled: true` gate the widget to EDU sites: it
    // stays disabled (greyed-out with a tooltip) until an EDU collection load
    // flips it available via `setWidgetAsAvailable` — the LOGIN_EDU pattern.
    params: {
        ...defaultWidgetParams,
        isEducation: true,
        disabled: true,
        disabledTooltip: 'edu_widget_disabled_tooltip',
        type: ['default', 'specific', 'zero'],
    },
    signalForm: true,
};
